import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronsUpDown, LayoutGrid } from "lucide-react";
import { DropdownMenuItem } from "@flint/ui";
import { meQueryOptions } from "../../lib/session.ts";
import { UserAvatar } from "../workspace/components/user-avatar.tsx";
import { AccountMenu } from "../workspace/components/account-menu.tsx";

/**
 * The signed-in user chip pinned to the bottom of the Store admin rail.
 *
 * Store admin hides the workspace topbar (`WorkspaceShell.hideTopbar`), so this
 * is the module's only account surface *and* its only way back to the rest of
 * Flint — hence the "Flint app" item, which points at the module launcher the
 * topbar's switcher would otherwise reach.
 *
 * Renders nothing until `me` resolves, so the rail never shows a blank chip.
 *
 * @see prototype/store/admin/all.html `.side-user` — the visual baseline
 */
export function StoreSidebarUser() {
  const { data: me } = useQuery(meQueryOptions);
  if (!me?.user) return null;

  return (
    <AccountMenu
      user={me.user}
      company={me.company}
      side="right"
      align="end"
      extraItems={
        <DropdownMenuItem asChild>
          <Link to="/launch" className="flex w-full items-center gap-2">
            <LayoutGrid size={14} strokeWidth={2} aria-hidden className="text-muted" />
            Flint app
          </Link>
        </DropdownMenuItem>
      }
    >
      {/* At the 56px icon width only the avatar survives; the dropdown still
          carries the full name, email and role, so nothing is lost. */}
      <button
        type="button"
        className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left transition-colors hover:bg-sidebar-accent group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0"
      >
        <UserAvatar name={me.user.name} src={me.user.avatarUrl} size="sm" />
        <span className="min-w-0 flex-1 group-data-[collapsible=icon]:hidden">
          <span className="block truncate text-xs font-semibold text-ink">{me.user.name}</span>
          <span className="block truncate text-xs text-muted">Store admin</span>
        </span>
        <ChevronsUpDown
          aria-hidden
          className="size-3.5 shrink-0 text-muted group-data-[collapsible=icon]:hidden"
        />
      </button>
    </AccountMenu>
  );
}
